import React, {
    ChangeEvent,
    ClipboardEvent,
    FocusEvent,
    KeyboardEvent,
    ReactNode,
    useEffect,
    useRef,
    useState
} from 'react';
import InfoIcon from '../../../../ui/svg/common/info';
import { DEFAULT_ERROR } from '../../../../DAL/data-provider';
import { IBrigerPayValidation } from '../../cart-bridger-pay/interfaces';
import { createCashierToken } from '../../cart-bridger-pay/data/data-service';
import { IFormControl } from '../../../../ui/forms/interfaces';
import Preloader from '../../../../ui/svg/common/preloader';
import FormTextField from '../../../../ui/forms/fields/text-field';
import RememberMePopup from './remember-me-popup';
import { createCashierTokenOmc } from '../../cart-omc-bridger-pay/data/data-service';
import { BRIDGER_PAY_VALIDATION_EVENT } from '../../cart-bridger-pay/domain/shopping-cart-events-provider';

/**
 * https://developers.bridgerpay.com/docs/blox-card-tokenization-sdk
 */
interface INewCreditCardShared {
    isSubscription: boolean;
    setBloxController: (_bloxController: any) => void;

    rememberMe: boolean;
    rememberMeOnChangeEvent?: (evt: ChangeEvent<HTMLInputElement>) => void;
    hideRememberMe?: boolean;

    nameOnCardChangeEvent: (evt: ChangeEvent) => void;
    nameOnCardPasteEvent: (evt: ClipboardEvent<HTMLInputElement>) => void;
    nameOnCardBlurEvent: (evt: FocusEvent) => void;
    nameOnCardKeyupEvent: (ect: KeyboardEvent) => void;
    nameOnCardControl: IFormControl;

    showCvvInfoPopup?: () => void;

    back?: () => void;
    showBackButton: boolean;
    backText?: string;

    topHtmlOutsideForm?: ReactNode;
    bottomHtmlOutsideForm?: ReactNode;
    topHtmlInForm?: ReactNode;
    bottomHtmlInForm?: ReactNode;

    isOmc?: boolean;
}

const NewCreditCardShared = (props: INewCreditCardShared) => {

    const {
        isSubscription,  setBloxController, rememberMe,
    } = props;

    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState('');

    const [cardNumberBP, setCardNumberBP] = useState<IBrigerPayValidation|null>(null);
    const [expirationBP, setExpirationBP] = useState<IBrigerPayValidation|null>(null);
    const [cvvBP, setCvvBP] = useState<IBrigerPayValidation|null>(null);

    const creditErrorRef = useRef<HTMLDivElement>(null);
    const expErrorRef = useRef<HTMLDivElement>(null);
    const cvvErrorRef = useRef<HTMLDivElement>(null);

    const nameOnCardTextboxRef = useRef<HTMLInputElement>(null);

    /*
    const testLog = (msg: string) => {
        console.log(msg);
        const $el = document.querySelector('#test-log-ph');
        if(!$el) return;
        const $div = document.createElement('div') as HTMLElement;
        $div.textContent = msg;

        $el.append($div)
    };*/

    useEffect(() => {

        (async () => {
            setLoading(true);

            const response = props.isOmc ? await createCashierTokenOmc() : await createCashierToken();

            if(!response || response.statusCode !== 200 || !response.data.isValid || !response?.data?.relatedData?.cashierToken){
                setError('An error has occurred.');
                setLoading(false);
                return;
            }

            // testLog('after ajax ' + (new Date()));

            const cashierToken = response.data.relatedData.cashierToken;

            const _bloxController = new window.BP.BloxController({
                token: cashierToken,
                fontsSources: ['https://fonts.googleapis.com/css2?family=Roboto&display=swap'],
                style: {
                    base: {
                        font: "16px/1.5 Roboto, Arial",
                        color: '#1D1D1D',
                        ':focus': {
                            color: '#121210',
                        },
                        fontWeight: 400,
                    },
                    invalid: {
                        color: '#ff6666',
                    },
                },
            });

            const cardNumber = _bloxController.createBlox('cardNumber', {
                placeholder: 'Enter Card Number',
                style: {
                    base: {
                        color: '#1D1D1D',
                        fontWeight: 400,
                        caretColor: '#000',
                    },
                },
            });

            cardNumber.on(
                'change',
                (result: IBrigerPayValidation) => {
                    setCardNumberBP(result);
                }
            );

            cardNumber.on('validate', (result: IBrigerPayValidation) => {
                setCardNumberBP(result);
            });

            const cardExpiryDate = _bloxController.createBlox('cardExpiryDate', {
                placeholder: 'MM / YY',
                style: {
                    base: {
                        color: '#1D1D1D',
                        caretColor: '#000',
                    },
                },
            });

            // testLog('after create blox API ' + (new Date()));

            cardExpiryDate.on(
                'change',
                (result: IBrigerPayValidation) => {
                    setExpirationBP(result);
                }
            );

            cardExpiryDate.on('validate', (result: IBrigerPayValidation) => {
                setExpirationBP(result);
            });

            const cardCvv = _bloxController.createBlox('cardCvv', {
                placeholder: 'CVV',
                unmaskedCVV: true,
                style: {
                    base: {
                        color: '#1D1D1D',
                        caretColor: '#000',
                    },
                },

            });

            cardCvv.on(
                'change',
                (result: IBrigerPayValidation) =>{
                    setCvvBP(result);
                }
            );

            cardCvv.on('validate', (result: IBrigerPayValidation) => {
                setCvvBP(result);
            });

            setBloxController(_bloxController);

            try{
                await Promise.all([
                    cardNumber.mount('#bp-card-number'),
                    cardExpiryDate.mount('#bp-exp-date'),
                    cardCvv.mount('#bp-cvv'),
                ]);

                // testLog('after Promise all ' + (new Date()));
            }
            catch (ex) {
                console.log('Error', ex);
            }

            setLoading(false);

            window.setTimeout(() => {
                nameOnCardTextboxRef.current?.focus();
            }, 100);

        })();
    },
        // eslint-disable-next-line
        []
    );

    const isCreditCardError = cardNumberBP && cardNumberBP.invalid; // cardNumberBP.touched &&
    const isExpError = expirationBP && expirationBP.invalid; // expirationBP.touched &&
    const isCvvError = !!cvvBP && cvvBP.invalid; //  && cvvBP.touched
    const [ rememberMePopupShown, setRememberMePopupShown ] = useState(false);

    useEffect(() => {
        const handleValidationFromOutside = (_evt: CustomEvent) => {
            if(isCreditCardError) {
                creditErrorRef.current?.focus();
                return;
            }

            if(isExpError) {
                expErrorRef.current?.focus();
                return;
            }

            if(isCvvError) {
                cvvErrorRef.current?.focus();
                return;
            }
        };

        // @ts-ignore
        document.addEventListener(BRIDGER_PAY_VALIDATION_EVENT, handleValidationFromOutside);

        return () => {
            // @ts-ignore
            document.removeEventListener(BRIDGER_PAY_VALIDATION_EVENT, handleValidationFromOutside);
        };
    }, [isCvvError, isExpError, isCreditCardError]);

    const showRememberMePopup = () => {
        setRememberMePopupShown(true);
    };

    const hideRememberMePopup = () => {
        setRememberMePopupShown(false);
    };

    return (
        <div>
            {
                props.showBackButton &&
                <button type="button" className="text-tiny font-bold text-blue-200 mb-4" onClick={ props.back }>
                    { props.backText || 'My Saved Cards' }
                </button>
            }

            {/* Optional Top HTML */}
            {props.topHtmlOutsideForm}

            {
                loading &&
                <div className="flex justify-center">
                    <Preloader width="25px" height="25px" color="#4f4f4f" />
                </div>
            }

            {
                !error &&
                <div style={loading ? {
                    opacity: 0,
                } : {}}>

                    {/* Optional Top HTML */}
                    {props.topHtmlInForm}

                    <FormTextField
                        ref={ nameOnCardTextboxRef }
                        id={'nameOnCard'}
                        label={'Name on Card'}
                        control={props.nameOnCardControl}
                        maxLength={100}
                        rootClasses="mb-0"
                        qa={'cart-step3-card-holder-name-tb'}
                        textBoxClasses="rounded"
                        onChange={props.nameOnCardChangeEvent}
                        onBlur={props.nameOnCardBlurEvent}
                        onKeyUp={props.nameOnCardKeyupEvent}
                        onPaste={props.nameOnCardPasteEvent}
                    />

                    {/* Briger Pay Placeholders */}
                    <div>
                        <div className={`w-form-field ${isCreditCardError ? 'w-form-error' : ''}`}>
                            <div className="w-form-tb rounded" id="bp-card-number"/>
                        </div>

                        {
                            isCreditCardError &&
                            <div
                                tabIndex={-1}
                                ref={creditErrorRef}
                                className="text-red text-sm mb-4"
                                role="alert"
                                aria-live="assertive">{cardNumberBP.error || DEFAULT_ERROR}</div>
                        }

                        <div className={`w-form-field  ${isExpError ? 'w-form-error' : ''}`}>
                            <div className="w-form-tb rounded" id="bp-exp-date"/>
                        </div>

                        {
                            isExpError &&
                            <div
                                tabIndex={-1}
                                ref={expErrorRef}
                                className="text-red text-sm mb-4"
                                role="alert"
                                aria-live="assertive">{expirationBP.error || DEFAULT_ERROR}</div>
                        }

                        <div className={`w-form-field  ${isCvvError ? 'w-form-error' : ''}`}>
                            <div className="w-form-tb rounded" id="bp-cvv"/>
                        </div>

                        {
                            isCvvError &&
                            <div
                                tabIndex={-1}
                                ref={cvvErrorRef}
                                className="text-red text-sm mb-4"
                                role="alert"
                                aria-live="assertive">{cvvBP.error || DEFAULT_ERROR}</div>
                        }
                    </div>

                    <div className="flex items-center justify-between mt-2">
                        {/* Remember Me */}
                        <label
                            className={`flex items-center text-gray-500 ${(isSubscription || props.hideRememberMe) ? 'hidden' : ''}`}>
                            <input
                                aria-label="Remember me"
                                type="checkbox"
                                checked={isSubscription ? true : rememberMe}
                                onChange={props.rememberMeOnChangeEvent}
                            />

                            <span className="mx-2">Remember me</span>
                            <button type="button" onClick={showRememberMePopup} title="Learn more about remember me">
                                <InfoIcon width="15px" color="#777"/>
                            </button>
                        </label>

                        <button
                            type="button"
                            className="text-sm text-gray-500 flex items-center ml-auto"
                            onClick={props.showCvvInfoPopup}>
                            <span className="mr-2">What is CVV?</span>
                            <InfoIcon width="15px" color="#777"/>
                        </button>
                    </div>

                    {/* Optional Bottom HTML */}
                    {props.bottomHtmlInForm}
                </div>
            }

            {/* Optional Bottom HTML */}
            {props.bottomHtmlOutsideForm}

            {
                !loading && error && <div className="error-box"><p>{error}</p></div>
            }

            {rememberMePopupShown && <RememberMePopup onClose={hideRememberMePopup}/>}
        </div>
    )
};

export default NewCreditCardShared;
// export default forwardRef<IOverviewMapTextElementApi, IOverviewMapTextElementProps>(OverviewMapTextElement);